import { usePageView } from '@fs/zion-analytics'
import { useParams } from '@fs/zion-router'
import { Separator, H3 } from '@fs/zion-ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { usePageTitle } from '../../app/PageTitleContext'
import { timestampToDateString } from '../../../util/DateUtil'
import { enumToReadableCamelCase, getBooleanDisplayValue } from '../../../util/StringUtil'
import DataView from '../../common/DataView'
import usePrintableInventoryName from './usePrintableInventoryName'
import { PersonLink, UnitLink } from '../../app/Links'

export default function PrintableInventoryName() {
  usePageView({ page_detail: 'DMConsole:PrintableInventoryName' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.temple.printableInventoryName'))
  const { pinId } = useParams()
  const printableInventoryName = usePrintableInventoryName(pinId)

  if (!pinId) {
    return <>{t('temple.printableInventoryName.lookup', 'Look up a PIN ID to view printable inventory name')}</>
  }
  if (!printableInventoryName) {
    return null
  }
  const pinSummary = [
    {
      label: t('ordinanceType', 'Ordinance Type'),
      detail: enumToReadableCamelCase(printableInventoryName.ordinanceType),
    },
    {
      label: t('transliterationNeeded', 'Transliteration Needed'),
      detail: getBooleanDisplayValue(printableInventoryName.transliterationNeeded),
    },
    {
      label: t('createdDate', 'Created Date'),
      detail: timestampToDateString({
        timestamp: printableInventoryName.createDate,
        withTime: true,
        timestampIsSeconds: true,
      }),
    },
    {
      label: t('submissionType', 'Submission Type'),
      detail: enumToReadableCamelCase(printableInventoryName.submissionType),
    },
    { label: t('unit.type.TEMPLE', 'Temple'), detail: <UnitLink unitnumber={printableInventoryName.templeDistrict} /> },
    {
      label: t('updateDate', 'Update Date'),
      detail: timestampToDateString({
        timestamp: printableInventoryName.updateDate,
        withTime: true,
        timestampIsSeconds: true,
      }),
    },
    { label: t('stake', 'Stake'), detail: <UnitLink unitnumber={printableInventoryName.stake} /> },
    { label: t('ward', 'Ward'), detail: <UnitLink unitnumber={printableInventoryName.ward} /> },
    {
      label: t('sharedDate', 'Shared Date'),
      detail: timestampToDateString({
        timestamp: printableInventoryName.sharedDate,
        withTime: true,
        timestampIsSeconds: true,
      }),
    },
    { label: null, detail: null },
    { label: null, detail: null },
    {
      label: t('holdExpirationDate', 'Hold Expiration Date'),
      detail: printableInventoryName.holdExpirationDate
        ? timestampToDateString({
            timestamp: printableInventoryName.holdExpirationDate,
            withTime: true,
            timestampIsSeconds: true,
          })
        : null,
    },
  ]
  const principalSummary = printableInventoryName.principal && [
    {
      label: t('pid', 'PID'),
      detail: <PersonLink personId={printableInventoryName.principal.treePersonId} />,
    },
    {
      label: t('sex', 'Sex'),
      detail: enumToReadableCamelCase(printableInventoryName.principal.sex),
    },
    {
      label: null,
      detail: null,
    },
  ]
  const wifeSummary = printableInventoryName.wife && [
    {
      label: t('pid', 'PID'),
      detail: <PersonLink personId={printableInventoryName.wife.treePersonId} />,
    },
    {
      label: t('sex', 'Sex'),
      detail: enumToReadableCamelCase(printableInventoryName.wife.sex),
    },
    {
      label: null,
      detail: null,
    },
  ]
  const fatherSummary = printableInventoryName.father && [
    {
      label: t('pid', 'PID'),
      detail: <PersonLink personId={printableInventoryName.father.treePersonId} />,
    },
    {
      label: t('sex', 'Sex'),
      detail: enumToReadableCamelCase(printableInventoryName.father.sex),
    },
    {
      label: null,
      detail: null,
    },
  ]
  const motherSummary = printableInventoryName.mother && [
    {
      label: t('pid', 'PID'),
      detail: <PersonLink personId={printableInventoryName.mother.treePersonId} />,
    },
    {
      label: t('sex', 'Sex'),
      detail: enumToReadableCamelCase(printableInventoryName.mother.sex),
    },
    {
      label: null,
      detail: null,
    },
  ]

  const getNamePartLabel = (namePartType) => {
    if (namePartType === 'PREFIX') return t('name.parts.prefix', 'Prefix')
    if (namePartType === 'GIVEN') return t('name.parts.given', 'Given')
    if (namePartType === 'SURNAME') return t('name.parts.surname', 'Surname')
    if (namePartType === 'SUFFIX') return t('name.parts.suffix', 'Suffix')
    return null
  }

  const addNames = (nameForms, summary) => {
    nameForms.forEach((nameForm) => {
      nameForm.nameParts.forEach((namePart) => {
        summary.push({
          label: `${getNamePartLabel(namePart.namePartType)} (${nameForm.lang})`,
          detail: namePart.value,
        })
      })
      for (let i = 0; i < 4 - nameForm.nameParts.length; i++) {
        if (i === 2) {
          break
        }
        summary.push({
          label: null,
          detail: null,
        })
      }
    })
  }

  printableInventoryName.principal && addNames(printableInventoryName.principal.nameForms, principalSummary)
  printableInventoryName.wife && addNames(printableInventoryName.wife.nameForms, wifeSummary)
  printableInventoryName.father && addNames(printableInventoryName.father.nameForms, fatherSummary)
  printableInventoryName.mother && addNames(printableInventoryName.mother.nameForms, motherSummary)

  return (
    <>
      <H3 size="H5">{t('temple.subnav.inventoryInfo', 'Inventory Info')}</H3>
      <DataView id="Inventory Info" data={pinSummary} />
      <Separator size="xs" />
      <H3 size="H5">{t('principal', 'Principal')}</H3>
      <DataView id="Principal Summary" data={principalSummary} />
      {printableInventoryName.wife && (
        <>
          <Separator size="xs" />
          <H3 size="H5">{t('wife', 'Wife')}</H3>
          <DataView id="Wife Summary" data={wifeSummary} />
        </>
      )}
      {printableInventoryName.father && (
        <>
          <Separator size="xs" />
          <H3 size="H5">{t('father', 'Father')}</H3>
          <DataView id="Father Summary" data={fatherSummary} />
        </>
      )}
      {printableInventoryName.mother && (
        <>
          <Separator size="xs" />
          <H3 size="H5">{t('mother', 'Mother')}</H3>
          <DataView id="Mother Summary" data={motherSummary} />
        </>
      )}
    </>
  )
}
